import iso31662 from "iso-3166-2";

/**
 * Thin wrapper over the `iso-3166-2` package that returns a sorted, typed list
 * of subdivisions for a given alpha-2 country code. Used as a fallback when the
 * catalog has no state-scoped cash payers (ANY-state corridors) so the user can
 * still pick any subdivision.
 */
export interface StateOption {
  /** Bare subdivision code (e.g. `JAL` from `MX-JAL`). */
  code: string;
  /** Full ISO 3166-2 code (e.g. `MX-JAL`). */
  fullCode: string;
  /** Display name. */
  name: string;
}

const cache = new Map<string, StateOption[]>();

export const statesForCountry = (
  countryAlpha2: string | undefined | null
): StateOption[] => {
  if (!countryAlpha2) return [];
  const upper = countryAlpha2.toUpperCase();
  const cached = cache.get(upper);
  if (cached) return cached;

  // The runtime shape of `iso31662.country()` is
  // `{ name, sub: { 'XX-YYY': {...} } }` but the `@types/iso-3166-2` package is
  // incomplete, so we narrow it manually.
  const country = (
    iso31662 as unknown as {
      country: (
        code: string
      ) => { sub?: Record<string, { name: string; type: string }> } | null;
    }
  ).country(upper);

  if (!country?.sub) {
    cache.set(upper, []);
    return [];
  }

  const items: StateOption[] = Object.entries(country.sub).map(
    ([fullCode, sub]) => {
      const code = fullCode.split("-")[1] ?? fullCode;
      return { code, fullCode, name: sub.name };
    }
  );
  items.sort((a, b) => a.name.localeCompare(b.name));
  cache.set(upper, items);
  return items;
};

/** Resolve a human-readable subdivision name from ISO 3166-2 (fallback: code). */
export const stateName = (
  countryAlpha2: string | undefined | null,
  stateCode: string | undefined | null
): string => {
  if (!stateCode) return "";
  const list = statesForCountry(countryAlpha2);
  const match = list.find(
    (s) => s.code.toUpperCase() === stateCode.toUpperCase()
  );
  return match?.name ?? stateCode;
};

/**
 * Cash state picker options — same ISO-first rule as smart:
 * ANY-state → full ISO list; catalog → prefer ISO name by code, else backend name.
 */
export const resolveStateOptions = (
  country: string | undefined | null,
  catalogStates:
    | Array<{ stateCode: string; stateName: string }>
    | null
    | undefined,
  isAnyStateMode: boolean
): Array<{ code: string; name: string }> => {
  if (isAnyStateMode) {
    return statesForCountry(country).map((s) => ({
      code: s.code,
      name: s.name,
    }));
  }
  const isoByCode = new Map(
    statesForCountry(country).map((s) => [s.code.toUpperCase(), s.name])
  );
  return (catalogStates ?? []).map((s) => ({
    code: s.stateCode,
    name: isoByCode.get(s.stateCode?.toUpperCase()) ?? s.stateName,
  }));
};
